/**
 * الصلاحيات الفعلية في الواجهة — مصدر واحد لكل القوائم والمسارات والأزرار.
 *
 * القاعدة: ما لا يملك المستخدم صلاحيته لا يُعرض أصلاً (Unauthorized = Hidden).
 * الإخفاء هنا لتجربة الاستخدام فقط؛ الحماية الفعلية في الخلفية (403).
 *
 * الصلاحيات تُحمَّل عند الدخول ثم تُحدَّث من الخلفية (AccessControl/my) عند فتح
 * التطبيق وكل دقيقة وعند العودة للنافذة — فسحب صلاحية يسري دون إعادة دخول.
 */
import { useEffect, useState } from "react";
import axiosInstance from "../api/apiClient";
import { getUserSession, setUserSession } from "../function/AuthStorage";

const listeners = new Set();
let syncing = null;
let timer = null;

function session() {
  return getUserSession() || {};
}

export function isSuperAdmin() {
  const s = session();
  if (typeof s.isSuperAdmin === "boolean") return s.isSuperAdmin;
  return s.userType === "admin" || String(s.role || "").toLowerCase() === "admin";
}

export function getPermissions() {
  return session().permissions || [];
}

/** هل يملك المستخدم الصلاحية؟ مدير النظام يملك كل شيء. */
export function can(key) {
  if (!key) return true;
  if (isSuperAdmin()) return true;
  return getPermissions().includes(key);
}

export function canAny(keys) {
  if (!keys || keys.length === 0) return true;
  if (isSuperAdmin()) return true;
  const mine = getPermissions();
  return keys.some((k) => mine.includes(k));
}

/** requirement: { permission } | { anyOf } | { superAdmin: true } | null */
export function meets(requirement) {
  if (!requirement) return true;
  if (requirement.superAdmin) return isSuperAdmin();
  if (requirement.permission) return can(requirement.permission);
  if (requirement.anyOf) return canAny(requirement.anyOf);
  return true;
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* مستمع معطوب لا يوقف البقية */
    }
  });
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** يجلب الصلاحيات الفعلية من الخلفية ويحدّث الجلسة إن تغيّرت. */
export async function refreshAccess() {
  const current = getUserSession();
  if (!current?.token) return null;
  if (syncing) return syncing;
  syncing = axiosInstance
    .get("AccessControl/my")
    .then(({ data }) => {
      const latest = getUserSession();
      // رد غير متوقع (نسخة العرض أو خادم أقدم): تبقى الصلاحيات كما هي
      if (!latest?.token || !data || !Array.isArray(data.permissions) || typeof data.isSuperAdmin !== "boolean") return null;
      const next = {
        ...latest,
        permissions: data.permissions || [],
        isSuperAdmin: !!data.isSuperAdmin,
        workTypes: data.workTypes ?? null,
        templateName: data.templateName ?? null,
      };
      const changed =
        JSON.stringify(latest.permissions || []) !== JSON.stringify(next.permissions) ||
        latest.isSuperAdmin !== next.isSuperAdmin ||
        JSON.stringify(latest.workTypes ?? null) !== JSON.stringify(next.workTypes) ||
        (latest.templateName ?? null) !== next.templateName;
      if (changed) {
        setUserSession(next);
        emit();
      }
      return next;
    })
    .catch(() => null)
    .finally(() => {
      syncing = null;
    });
  return syncing;
}

/** يبدأ المزامنة الدورية (مرة واحدة للتطبيق). */
export function startAccessSync() {
  if (timer) return;
  refreshAccess();
  timer = setInterval(refreshAccess, 60000);
  window.addEventListener("focus", refreshAccess);
  window.addEventListener("storage", (e) => {
    if (e.key === "user") emit();
  });
}

/** يعيد الرسم عند تغيّر الصلاحيات. */
export function useAccess() {
  const [, setTick] = useState(0);
  useEffect(() => subscribe(() => setTick((n) => n + 1)), []);
  return { can, canAny, meets, isSuperAdmin: isSuperAdmin(), permissions: getPermissions() };
}
